import {useMemo, useState} from "react";
import {useForm, Controller} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import * as Yup from "yup";
import {App as AntdApp, Alert, Select, Table, Tag, Tooltip} from "antd";
import className from "classnames/bind";
import {Button, FontAwesomeIcon} from "~/components";
import {InputField} from "~/components/Forms";
import {rtkErrorMessage} from "~/reduxs/api/apiSlice";
import {useCompetitorContentGapMutation, useGetCompetitorsQuery} from "~/reduxs/api/competitorApiSlice";
import {useAddKeywordItemsMutation} from "~/reduxs/api/keywordApiSlice";
import style from "../style/Competitor.module.scss";

const cn = className.bind(style);

/** Trần đối thủ so cùng lúc (khớp GAP_MAX_COMPETITORS phía BE). */
const MAX_RIVALS = 5;

const schema = Yup.object().shape({
	own_url: Yup.string().trim()
		.required('Vui lòng nhập URL trang của bạn')
		.matches(/^https?:\/\//i, 'URL phải bắt đầu bằng http:// hoặc https://'),
});

/**
 * Tab "Content Gap" — so trang CỦA MÌNH với trang của NHIỀU đối thủ cùng lúc:
 * chọn đối thủ (mặc định phân tích URL đại diện/homepage, có thể chỉ định URL bài cụ thể
 * từng đối thủ) → cụm từ có ở ≥N đối thủ mà trang mình thiếu/mỏng, kèm số liệu GSC thật
 * + lưu vào Kho từ khoá. Trang đối thủ live-fetch tốn 1 lượt kiểm tra URL (cache 24h
 * miễn phí); hết lượt giữa chừng → kết quả PARTIAL, nguồn bị bỏ qua có tag riêng.
 */
function CompetitorContentGap({projectId}) {

	const {notification} = AntdApp.useApp();
	const {data: list} = useGetCompetitorsQuery(projectId);
	const [runGap, {isLoading: comparing}] = useCompetitorContentGapMutation();
	const [addItems, {isLoading: saving}] = useAddKeywordItemsMutation();

	const [selectedIds, setSelectedIds] = useState([]);
	const [targets, setTargets] = useState({});        // {competitorId: url bài cụ thể}
	const [minCompetitors, setMinCompetitors] = useState(null);
	const [result, setResult] = useState(null);
	const [savedLocal, setSavedLocal] = useState(() => new Set());
	const [savingKey, setSavingKey] = useState(null);

	const {control, handleSubmit, formState: {errors}} = useForm({
		defaultValues: {own_url: ''},
		resolver: yupResolver(schema),
	});

	const competitors = useMemo(() => list?.competitors || [], [list]);
	const options = competitors.map((c) => ({value: c.id, label: `${c.name || c.domain} (${c.domain})`}));
	const selected = competitors.filter((c) => selectedIds.includes(c.id));

	const onRun = async ({own_url}) => {
		if (selectedIds.length === 0) {
			notification.warning({message: 'Chọn đối thủ', description: 'Chọn ít nhất 1 đối thủ để so sánh.'});
			return;
		}
		try {
			const cleanTargets = {};
			selectedIds.forEach((id) => {
				const url = (targets[id] || '').trim();
				if (url) cleanTargets[id] = url;
			});
			const res = await runGap({
				projectId,
				own_url: own_url.trim(),
				competitor_ids: selectedIds,
				...(Object.keys(cleanTargets).length ? {targets: cleanTargets} : {}),
				...(minCompetitors ? {min_competitors: minCompetitors} : {}),
			}).unwrap();
			setResult(res);

			const skipped = (res?.sources || []).filter((s) => s.skipped === 'quota');
			if (skipped.length > 0) {
				notification.warning({
					message: 'Kết quả một phần',
					description: `${skipped.length} đối thủ bị bỏ qua vì hết lượt kiểm tra URL trong tuần.`,
				});
			}
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e, 'Không so sánh được')});
		}
	};

	const isSaved = (t) => t.saved || savedLocal.has(t.term);

	const saveTerm = async (t, key) => {
		try {
			setSavingKey(key);
			const res = await addItems({
				projectId,
				items: [{
					keyword: t.term,
					source: 'content',
					...(t.gsc ? {impressions: t.gsc.impressions, position: t.gsc.position} : {}),
					meta: {url: result?.base?.url, rivals: t.rivals},
				}],
			}).unwrap();
			setSavedLocal((prev) => new Set(prev).add(t.term));
			if (res?.added > 0) {
				notification.success({message: 'Đã lưu vào kho', description: t.term});
			} else {
				notification.info({message: 'Đã có', description: 'Cụm từ này đã có trong kho.'});
			}
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e, 'Không lưu được')});
		} finally {
			setSavingKey(null);
		}
	};

	const termCols = (keyPrefix, extra = []) => [
		{
			title: 'Cụm từ', dataIndex: 'term', key: 'term', ellipsis: true,
			render: (v, t) => (
				<span>
					{v}{' '}
					{isSaved(t) && <FontAwesomeIcon icon="fa-solid fa-circle-check" style={{color: '#16a34a'}} />}
				</span>
			),
		},
		{
			title: 'Số đối thủ dùng', dataIndex: 'rivals', key: 'rivals', width: 130, align: 'center',
			sorter: (a, b) => a.rivals - b.rivals, defaultSortOrder: 'descend',
			render: (v) => <Tag color={v > 1 ? 'volcano' : 'default'}>{v} đối thủ</Tag>,
		},
		// Cột phụ — ẩn dưới 768px cho gọn mobile.
		{title: 'Cụm', dataIndex: 'ngram', key: 'ngram', width: 80, align: 'center', responsive: ['md'], render: (v) => <Tag>{v} từ</Tag>},
		{title: 'Density TB đối thủ', dataIndex: 'avg_density', key: 'ad', width: 140, align: 'center', responsive: ['md'], render: (v) => `${v}%`},
		{
			title: 'GSC', key: 'gsc', width: 170, align: 'center',
			render: (_, t) => t.gsc ? (
				<Tooltip title={`${t.gsc.clicks} clicks (${t.gsc.from} → ${t.gsc.to})`}>
					<Tag color="green">{t.gsc.impressions.toLocaleString('vi-VN')} impr · vị trí {Number(t.gsc.position).toFixed(1)}</Tag>
				</Tooltip>
			) : <span className={cn('hint')}>—</span>,
		},
		...extra,
		{
			title: '', key: 'action', align: 'right', width: 60,
			render: (_, t) => (
				<Tooltip title={isSaved(t) ? 'Đã có trong kho' : 'Lưu vào kho từ khoá'}>
					<Button small white disabled={isSaved(t) || saving} loading={savingKey === `${keyPrefix}-${t.term}`}
						onClick={() => saveTerm(t, `${keyPrefix}-${t.term}`)}
						leftIcon={<FontAwesomeIcon icon="fa-light fa-bookmark" />} />
				</Tooltip>
			),
		},
	];

	const sourceTag = (s) => {
		if (s.ok) return <Tag color={s.cached ? 'blue' : 'green'}>{s.cached ? 'cache 24h (miễn phí)' : 'đã phân tích'}</Tag>;
		if (s.skipped === 'quota') return <Tag color="orange">hết lượt — bỏ qua</Tag>;
		return <Tooltip title={s.error}><Tag color="red">lỗi</Tag></Tooltip>;
	};

	if (competitors.length === 0) {
		return (
			<Alert type="info" showIcon
				message="Chưa có đối thủ nào"
				description='Sang tab "Đối thủ" thêm ít nhất 1 đối thủ trước khi so Content Gap.' />
		);
	}

	return (
		<div>
			<Alert type="info" showIcon style={{marginBottom: 14}}
				message={`Mỗi trang đối thủ live-fetch tốn 1 lượt "kiểm tra URL" của gói (kết quả cache 24h — phân tích lại trong ngày miễn phí). Tối đa ${MAX_RIVALS} đối thủ mỗi lượt so sánh.`} />

			{/* Scope class `form` để InputField ăn style chuẩn của site. */}
			<div className={cn('section') + ' form'}>
				<h3 className={cn('sectionTitle')}>Chọn trang so sánh</h3>

				<Controller control={control} name="own_url" render={({field}) => (
					<InputField label="URL trang CỦA BẠN (phải thuộc website dự án)"
						placeholder="https://website-cua-ban.vn/dich-vu-seo" errors={errors} {...field} />
				)} />

				<div className={cn('filterBar')}>
					<Select mode="multiple" style={{minWidth: 320, maxWidth: 560}} placeholder="Chọn đối thủ (tối đa 5)"
						options={options} value={selectedIds}
						onChange={(v) => setSelectedIds(v.slice(0, MAX_RIVALS))} />
					<Select style={{width: 220}} placeholder="Ngưỡng: có ở ≥N đối thủ"
						value={minCompetitors}
						onChange={setMinCompetitors}
						allowClear
						options={Array.from({length: Math.max(1, selectedIds.length)}, (_, i) => ({
							value: i + 1, label: `Có ở ≥${i + 1} đối thủ`,
						}))} />
				</div>

				{selected.length > 0 && (
					<div style={{marginBottom: 4}}>
						{selected.map((c) => (
							<InputField key={c.id}
								label={`URL bài của ${c.name || c.domain} (tuỳ chọn — bỏ trống dùng ${c.url || `https://${c.domain}`})`}
								placeholder={`https://${c.domain}/bai-viet-cung-chu-de`}
								value={targets[c.id] || ''}
								onChange={(e) => setTargets((prev) => ({...prev, [c.id]: e.target.value}))} />
						))}
					</div>
				)}

				<Button primary loading={comparing} onClick={handleSubmit(onRun)}
					leftIcon={<FontAwesomeIcon icon="fa-light fa-code-compare" />}>
					So sánh Content Gap ({selectedIds.length} đối thủ — tốn tối đa {selectedIds.length} lượt)
				</Button>
			</div>

			{result && (
				<div className={cn('section')}>
					<h3 className={cn('sectionTitle')}>
						Kết quả — trang gốc:{' '}
						<a href={result.base.url} target="_blank" rel="noreferrer">{result.base.title || result.base.url}</a>
					</h3>

					<div style={{display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12}}>
						{(result.sources || []).map((s, i) => (
							<span key={i} style={{display: 'inline-flex', alignItems: 'center', gap: 4}}>
								<span className={cn('hint')}>{s.name || `#${s.competitor_id}`}:</span> {sourceTag(s)}
							</span>
						))}
					</div>

					<p className={cn('hint')} style={{margin: '0 0 12px'}}>
						Ngưỡng: cụm có ở ≥{result.min_competitors} đối thủ · {result.missing.length} cụm bạn KHÔNG có
						· {result.weaker.length} cụm bạn phủ mỏng hơn mặt bằng đối thủ.
					</p>

					<h4 style={{fontSize: 14, fontWeight: 600, margin: '0 0 8px'}}>Đối thủ có — bạn chưa có ({result.missing.length})</h4>
					<Table rowKey="term" size="small"
						columns={termCols('missing')}
						dataSource={result.missing}
						scroll={{x: 'max-content'}}
						pagination={{pageSize: 15, hideOnSinglePage: true, showSizeChanger: false}} />

					{result.weaker.length > 0 && (
						<>
							<h4 style={{fontSize: 14, fontWeight: 600, margin: '16px 0 8px'}}>Bạn phủ mỏng hơn hẳn ({result.weaker.length})</h4>
							<Table rowKey="term" size="small"
								columns={termCols('weaker', [{
									title: 'Density của bạn', dataIndex: 'base_density', key: 'bd', width: 130, align: 'center',
									render: (v) => <span className={cn('hint')}>{v}%</span>,
								}])}
								dataSource={result.weaker}
								scroll={{x: 'max-content'}}
								pagination={{pageSize: 10, hideOnSinglePage: true, showSizeChanger: false}} />
						</>
					)}
				</div>
			)}
		</div>
	);
}

export default CompetitorContentGap;
